// iter 275 round 42 — Hook condiviso per ordinare le foto della brochure.
//
// Estratto da BrochureExportModal.jsx: classifica le foto come orizzontali
// vs verticali in base alle dimensioni naturali, mette le verticali in
// fondo (preservando l'ordine originale dell'agente sulle orizzontali).
// La PRIMA foto (hero) resta sempre prima.
//
// Questo è critico per la cover della brochure A4: il layout aspetta una
// foto orizzontale come hero, altrimenti il testo si sovrappone all'img.
//
// Usato sia da BrochureExportModal (Annunci → Scarica file → Brochure) sia
// da Social Studio (programma open house → Brochure A4) per avere lo
// STESSO risultato visivo.

import { useEffect, useMemo, useState } from "react";

export function useSortedBrochurePhotos(rawPhotos, maxCount = 10) {
  // sortedPhotos === null = ancora in detection (img.onload pending)
  const [sortedPhotos, setSortedPhotos] = useState(null);

  useEffect(() => {
    if (!rawPhotos?.length) {
      setSortedPhotos([]);
      return;
    }
    let cancelled = false;
    const capped = rawPhotos.slice(0, maxCount);

    // iter 275 round 44: se il backend ha già fornito width/height o
    // direttamente il flag isVertical, skippiamo completamente il caricamento
    // delle immagini. Sort istantaneo.
    const allHaveDims = capped.every(
      (p) => typeof p.isVertical === "boolean" || (p.width && p.height),
    );
    if (allHaveDims) {
      const classified = capped.map((p) => ({
        ...p,
        _vert: typeof p.isVertical === "boolean"
          ? p.isVertical
          : p.height > p.width * 1.05,
      }));
      const [hero, ...rest] = classified;
      const horizontals = rest.filter((p) => !p._vert);
      const verticals = rest.filter((p) => p._vert);
      setSortedPhotos([hero, ...horizontals, ...verticals]);
      return;
    }

    Promise.all(
      capped.map(
        (p) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () =>
              resolve({
                ...p,
                _vert: img.naturalHeight > img.naturalWidth * 1.05,
              });
            img.onerror = () => resolve({ ...p, _vert: false });
            img.src = p.url;
          }),
      ),
    ).then((classified) => {
      if (cancelled) return;
      const [hero, ...rest] = classified;
      const horizontals = rest.filter((p) => !p._vert);
      const verticals = rest.filter((p) => p._vert);
      setSortedPhotos([hero, ...horizontals, ...verticals]);
    });

    return () => {
      cancelled = true;
    };
  }, [rawPhotos, maxCount]);

  return sortedPhotos;
}

/**
 * Property "patched" con foto ordinate per la brochure.
 * Usa il sortedPhotos del hook sopra. Se ancora `null`, ritorna la property
 * originale (così il preview rende con l'ordine originale finché il
 * detection non è completato — meglio che mostrare placeholder vuoto).
 */
export function usePatchedBrochureProperty(property, photoTotal) {
  const sorted = useSortedBrochurePhotos(property?.photos);
  return useMemo(() => {
    if (!property) return null;
    if (sorted == null) return property;
    return {
      ...property,
      photos: sorted,
      photoTotalCount: property.photoTotalCount || photoTotal || sorted.length,
    };
  }, [property, sorted, photoTotal]);
}
